import React, { useState, useEffect } from 'react';
import { CenteredModal } from "~/components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope, faRobot, faDatabase, faServer, faPlay, faCheckCircle, faTimesCircle, faVial, faExclamationTriangle, faArrowLeft, faMicrophone } from "@fortawesome/free-solid-svg-icons";
import cx from 'classnames';
import systemTestingApi from '~/api/systemTestingApi';
import qdrantHealthApi from '~/api/qdrantHealthApi';
import pcm16Base64ToFloat32 from '~/pages/btai-chatbots/AIChatbot/utils/pcm16Base64ToFloat32';
import * as signalR from "@microsoft/signalr";

function SystemServicesTestingModal({ show, onClose }) {
    const [activeTab, setActiveTab] = useState('hub');
    
    // States for Voice Audio
    const [testingVoiceAudio, setTestingVoiceAudio] = useState(false);
    const [voiceAudioResult, setVoiceAudioResult] = useState(null);
    
    // States for Mailjet
    const [mailjetEmail, setMailjetEmail] = useState('');
    const [testingMailjet, setTestingMailjet] = useState(false);
    const [mailjetResult, setMailjetResult] = useState(null);

    // States for SMTP
    const [smtpEmail, setSmtpEmail] = useState('');
    const [testingSmtp, setTestingSmtp] = useState(false);
    const [smtpResult, setSmtpResult] = useState(null);

    // States for OpenAI
    const [testingOpenAI, setTestingOpenAI] = useState(false);
    const [openAIResult, setOpenAIResult] = useState(null);

    // States for Database
    const [testingDatabase, setTestingDatabase] = useState(false);
    const [databaseResult, setDatabaseResult] = useState(null);

    // States for Qdrant
    const [loadingQdrant, setLoadingQdrant] = useState(false);
    const [qdrantData, setQdrantData] = useState(null);

    useEffect(() => {
        if (show && activeTab === 'qdrant' && !qdrantData) {
            fetchQdrantHealth();
        }
    }, [show, activeTab]);

    const handleTabChange = (tab) => {
        setActiveTab(tab);
    };

    const runMailjetTest = async () => {
        if (!mailjetEmail) return;
        setTestingMailjet(true);
        setMailjetResult(null);
        try {
            const res = await systemTestingApi.testMailjet(mailjetEmail);
            if (res.Succeeded || res.succeeded) {
                setMailjetResult({ status: 'success', message: 'Test email sent successfully via Mailjet!' });
            } else {
                setMailjetResult({ status: 'error', message: res.Message || res.message || 'Failed to send test email.' });
            }
        } catch (error) {
            setMailjetResult({ status: 'error', message: error.response?.data?.Message || 'API error occurred.' });
        }
        setTestingMailjet(false);
    };

    const runSmtpTest = async () => {
        if (!smtpEmail) return;
        setTestingSmtp(true);
        setSmtpResult(null);
        try {
            const res = await systemTestingApi.testSmtp(smtpEmail);
            if (res.Succeeded || res.succeeded) {
                setSmtpResult({ status: 'success', message: 'Test email sent successfully via SMTP!' });
            } else {
                setSmtpResult({ status: 'error', message: res.Message || res.message || 'Failed to send test email.' });
            }
        } catch (error) {
            setSmtpResult({ status: 'error', message: error.response?.data?.Message || 'API error occurred.' });
        }
        setTestingSmtp(false);
    };

    const runOpenAITest = async () => {
        setTestingOpenAI(true);
        setOpenAIResult(null);
        try {
            const res = await systemTestingApi.testOpenAI();
            if (res.Succeeded || res.succeeded) {
                setOpenAIResult({ status: 'success', message: 'OpenAI API is reachable and API Key is valid!' });
            } else {
                setOpenAIResult({ status: 'error', message: res.Message || res.message || 'OpenAI test failed.' });
            }
        } catch (error) {
            setOpenAIResult({ status: 'error', message: error.response?.data?.Message || 'API error occurred.' });
        }
        setTestingOpenAI(false);
    };

    const runDatabaseTest = async () => {
        setTestingDatabase(true);
        setDatabaseResult(null);
        try {
            const res = await systemTestingApi.testDatabase();
            if (res.Succeeded || res.succeeded) {
                setDatabaseResult({ status: 'success', message: `Database connected successfully! Latency: ${res.Data || res.data} ms` });
            } else {
                setDatabaseResult({ status: 'error', message: res.Message || res.message || 'Database connection failed.' });
            }
        } catch (error) {
            setDatabaseResult({ status: 'error', message: error.response?.data?.Message || 'API error occurred.' });
        }
        setTestingDatabase(false);
    };

    const runVoiceAudioTest = async () => {
        setTestingVoiceAudio(true);
        setVoiceAudioResult(null);
        try {
            const apiUrl = process.env.REACT_APP_API_URL;
            const conn = new signalR.HubConnectionBuilder()
                .withUrl(`${apiUrl}/api/voiceChatHub`, { withCredentials: true })
                .build();
                
            conn.on("ReceiveAudioChunkVoiceChat", (base64Pcm) => {
                setVoiceAudioResult({ status: 'success', message: `Audio chunk received via SignalR. Playing audio...` });
                const float32 = pcm16Base64ToFloat32(base64Pcm);
                if (float32.length > 0) {
                    const audioContext = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 24000 });
                    const buffer = audioContext.createBuffer(1, float32.length, 24000);
                    buffer.copyToChannel(float32, 0);
                    const source = audioContext.createBufferSource();
                    source.buffer = buffer;
                    source.connect(audioContext.destination);
                    source.start(0);
                    source.onended = () => {
                        audioContext.close();
                        conn.stop();
                        setTestingVoiceAudio(false);
                    };
                } else {
                    setVoiceAudioResult({ status: 'error', message: 'Received empty audio chunk.' });
                    conn.stop();
                    setTestingVoiceAudio(false);
                }
            });

            await conn.start();
            await conn.invoke("TestVoiceChatQuestionAudio");
        } catch (error) {
            setVoiceAudioResult({ status: 'error', message: error.toString() || 'SignalR connection error.' });
            setTestingVoiceAudio(false);
        }
    };

    const fetchQdrantHealth = async () => {
        setLoadingQdrant(true);
        try {
            const res = await qdrantHealthApi.getHealth();
            if (res.Succeeded || res.succeeded) {
                setQdrantData(res.Data || res.data);
            }
        } catch (error) {
            setQdrantData({ Status: "Error", PingMs: 0 });
        }
        setLoadingQdrant(false);
    };

    const getQdrantBadge = (status) => {
        if (status?.toLowerCase() === 'healthy' || status?.toLowerCase() === 'green') {
            return <span className="badge bg-success fs-9"><FontAwesomeIcon icon={faCheckCircle} className="me-1"/>{status}</span>;
        } else if (status?.toLowerCase() === 'yellow') {
            return <span className="badge bg-warning text-dark fs-9"><FontAwesomeIcon icon={faExclamationTriangle} className="me-1"/>{status}</span>;
        }
        return <span className="badge bg-danger fs-9"><FontAwesomeIcon icon={faTimesCircle} className="me-1"/>{status || 'Unhealthy'}</span>;
    };

    const renderResult = (result) => {
        if (!result) return null;
        return (
            <div className={`alert ${result.status === 'success' ? 'alert-success' : 'alert-danger'} mt-3 py-2 px-3 fs-9 d-flex align-items-center mb-0`}>
                <FontAwesomeIcon icon={result.status === 'success' ? faCheckCircle : faTimesCircle} className="me-2" />
                {result.message}
            </div>
        );
    };

    return (
        <CenteredModal
            show={show}
            title={
                <span><FontAwesomeIcon icon={faVial} className="me-2 text-primary" /> System Services Testing</span>
            }
            onClose={onClose}
            closeText="Close"
            modalType="modal-xl"
            isViewMode={true}
        >
            <div className="px-3 pb-3">
                {activeTab === 'hub' ? (
                    <>
                        <div className="mb-4 mt-2 px-3">
                            <h5 className="fw-bolder text-body-emphasis mb-2" style={{ letterSpacing: '-0.5px' }}>
                                System Services Testing
                            </h5>
                            <p className="text-muted fw-bold fs-8-1 mb-0">
                                Welcome to the System Services Testing panel. Select a service below to verify its connectivity and health status.
                            </p>
                        </div>
                        <div className="row row-cols-1 row-cols-md-2 row-cols-xl-3 g-4 px-3 mb-4">
                        {[
                            { id: 'mailjet', title: 'Mailjet', icon: faEnvelope, desc: 'Test integration with Mailjet Email API' },
                            { id: 'smtp', title: 'SMTP', icon: faEnvelope, desc: 'Test integration with configured SMTP server' },
                            { id: 'openai', title: 'OpenAI', icon: faRobot, desc: 'Verify connection to OpenAI language models' },
                            { id: 'database', title: 'SQL Server', icon: faDatabase, desc: 'Check primary database connectivity' },
                            { id: 'qdrant', title: 'Qdrant Vector', icon: faServer, desc: 'Test vector database connection and collections' },
                            { id: 'voiceaudio', title: 'Voice Chat Audio', icon: faMicrophone, desc: 'Test PCM16 voice chat audio playback' }
                        ].map((card) => (
                            <div className="col" key={card.id}>
                                <div 
                                    className="card h-100 shadow-sm cursor-pointer transition-all animated-border-card"
                                    onClick={() => handleTabChange(card.id)}
                                    style={{ transition: 'all 0.2s ease', borderRadius: '12px', border: 'none', padding: '2px', backgroundColor: 'transparent' }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.classList.add('shadow');
                                        e.currentTarget.style.transform = 'translateY(-3px)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.classList.remove('shadow');
                                        e.currentTarget.style.transform = 'none';
                                    }}
                                >
                                    <div className="card-body p-4 d-flex flex-column animated-border-card-inner" style={{ backgroundColor: '#fff', borderRadius: '10px' }}>
                                        <div className="d-flex justify-content-between align-items-start mb-3">
                                            <div className="p-3 bg-primary-subtle text-primary rounded-3 d-inline-flex align-items-center justify-content-center" style={{ width: '56px', height: '56px' }}>
                                                <FontAwesomeIcon icon={card.icon} size="lg" />
                                            </div>
                                        </div>
                                        <h5 className="card-title fw-bolder text-body-emphasis mt-2 mb-2" style={{ fontSize: '1.1rem' }}>{card.title}</h5>
                                        <p className="card-text text-muted fw-bold fs-8-1 mb-0 flex-grow-1" style={{ lineHeight: '1.6' }}>
                                            {card.desc}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        ))}
                        </div>
                    </>
                ) : (
                    <div className="mb-3 border-bottom d-flex align-items-center justify-content-between px-2 pb-2">
                        <h5 className="fw-bolder text-body-emphasis mb-0 fs-5">
                            {
                                activeTab === 'mailjet' ? 'Mailjet' :
                                activeTab === 'smtp' ? 'SMTP' :
                                activeTab === 'openai' ? 'OpenAI' :
                                activeTab === 'database' ? 'SQL Server' :
                                activeTab === 'qdrant' ? 'Qdrant Vector' :
                                activeTab === 'voiceaudio' ? 'Voice Chat Audio' : ''
                            }
                        </h5>
                        <button
                            className="btn btn-light border shadow-sm fw-bold d-flex align-items-center px-3 py-1"
                            onClick={() => handleTabChange('hub')}
                        >
                            <FontAwesomeIcon icon={faArrowLeft} className="me-2" /> Back to Hub
                        </button>
                    </div>
                )}

                <div className="tab-content px-2">
                    {/* MAILJET TAB */}
                    {activeTab === 'mailjet' && (
                        <div>
                            <p className="text-muted fs-9 mb-3">
                                Test the integration with Mailjet Email API. Enter an email address to send a test message.
                            </p>
                            <div className="d-flex align-items-center mb-3">
                                <input 
                                    type="email" 
                                    className="form-control fs-9 me-2" 
                                    placeholder="Enter recipient email..." 
                                    value={mailjetEmail} 
                                    onChange={(e) => setMailjetEmail(e.target.value)} 
                                />
                                <button className="btn btn-primary fs-9 d-flex align-items-center text-nowrap" onClick={runMailjetTest} disabled={testingMailjet || !mailjetEmail}>
                                    {testingMailjet ? <span className="spinner-border spinner-border-sm me-2" /> : <FontAwesomeIcon icon={faPlay} className="me-2" />}
                                    Run Test
                                </button>
                            </div>
                            {renderResult(mailjetResult)}
                        </div>
                    )}

                    {/* SMTP TAB */}
                    {activeTab === 'smtp' && (
                        <div>
                            <p className="text-muted fs-9 mb-3">
                                Test the integration with the configured SMTP server. Enter an email address to send a test message.
                            </p>
                            <div className="d-flex align-items-center mb-3">
                                <input 
                                    type="email" 
                                    className="form-control fs-9 me-2" 
                                    placeholder="Enter recipient email..." 
                                    value={smtpEmail} 
                                    onChange={(e) => setSmtpEmail(e.target.value)} 
                                />
                                <button className="btn btn-primary fs-9 d-flex align-items-center text-nowrap" onClick={runSmtpTest} disabled={testingSmtp || !smtpEmail}>
                                    {testingSmtp ? <span className="spinner-border spinner-border-sm me-2" /> : <FontAwesomeIcon icon={faPlay} className="me-2" />}
                                    Run Test
                                </button>
                            </div>
                            {renderResult(smtpResult)}
                        </div>
                    )}

                    {/* OPENAI TAB */}
                    {activeTab === 'openai' && (
                        <div>
                            <p className="text-muted fs-9 mb-3">
                                Test the OpenAI API connection. This will make a lightweight request to verify the API Key and account billing status.
                            </p>
                            <button className="btn btn-primary fs-9 d-flex align-items-center" onClick={runOpenAITest} disabled={testingOpenAI}>
                                {testingOpenAI ? <span className="spinner-border spinner-border-sm me-2" /> : <FontAwesomeIcon icon={faPlay} className="me-2" />}
                                Ping OpenAI
                            </button>
                            {renderResult(openAIResult)}
                        </div>
                    )}

                    {/* DATABASE TAB */}
                    {activeTab === 'database' && (
                        <div>
                            <p className="text-muted fs-9 mb-3">
                                Test the connection to the primary SQL Server database and measure the response latency.
                            </p>
                            <button className="btn btn-primary fs-9 d-flex align-items-center" onClick={runDatabaseTest} disabled={testingDatabase}>
                                {testingDatabase ? <span className="spinner-border spinner-border-sm me-2" /> : <FontAwesomeIcon icon={faPlay} className="me-2" />}
                                Ping SQL Server
                            </button>
                            {renderResult(databaseResult)}
                        </div>
                    )}

                    {/* QDRANT TAB */}
                    {activeTab === 'qdrant' && (
                        <div>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <p className="text-muted fs-9 mb-0">
                                    Live health metrics from the Qdrant Vector Database server.
                                </p>
                                <button className="btn btn-outline-primary btn-sm fs-9" onClick={fetchQdrantHealth} disabled={loadingQdrant}>
                                    {loadingQdrant ? <span className="spinner-border spinner-border-sm me-2" /> : <FontAwesomeIcon icon={faPlay} className="me-2" />}
                                    Refresh
                                </button>
                            </div>
                            {loadingQdrant && !qdrantData ? (
                                <div className="text-center py-4">
                                    <div className="spinner-border text-primary" />
                                </div>
                            ) : qdrantData ? (
                                <div className="table-responsive border rounded mt-2">
                                    <table className="table table-bordered table-hover mb-0 fs-9 align-middle">
                                        <tbody>
                                            <tr>
                                                <td className="bg-light fw-bold w-50 ps-3 py-3">Server Status</td>
                                                <td className="ps-3 py-3">{getQdrantBadge(qdrantData.Status)}</td>
                                            </tr>
                                            <tr>
                                                <td className="bg-light fw-bold ps-3 py-3">Latency (Ping)</td>
                                                <td className="ps-3 py-3">
                                                    <span className={`fw-bold ${qdrantData.PingMs > 1000 ? 'text-danger' : qdrantData.PingMs > 500 ? 'text-warning' : 'text-success'}`}>
                                                        {qdrantData.PingMs} ms
                                                    </span>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td className="bg-light fw-bold ps-3 py-3">Collection Status</td>
                                                <td className="ps-3 py-3">{getQdrantBadge(qdrantData.CollectionStatus)}</td>
                                            </tr>
                                            <tr>
                                                <td className="bg-light fw-bold ps-3 py-3">Total Vectors</td>
                                                <td className="fw-bold ps-3 py-3">{qdrantData.TotalVectors?.toLocaleString()}</td>
                                            </tr>
                                            <tr>
                                                <td className="bg-light fw-bold ps-3 py-3">Indexed Vectors</td>
                                                <td className="fw-bold text-success ps-3 py-3">{qdrantData.IndexedVectors?.toLocaleString()}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            ) : null}
                        </div>
                    )}

                    {/* VOICE AUDIO TAB */}
                    {activeTab === 'voiceaudio' && (
                        <div>
                            <p className="text-muted fs-9 mb-3">
                                Test the PCM16 voice chat audio playback. This will fetch the raw PCM audio file from the server and play it using the Web Audio API.
                            </p>
                            <button className="btn btn-primary fs-9 d-flex align-items-center" onClick={runVoiceAudioTest} disabled={testingVoiceAudio}>
                                {testingVoiceAudio ? <span className="spinner-border spinner-border-sm me-2" /> : <FontAwesomeIcon icon={faPlay} className="me-2" />}
                                Play Audio
                            </button>
                            {renderResult(voiceAudioResult)}
                        </div>
                    )}
                </div>
            </div>
        </CenteredModal>
    );
}

export default SystemServicesTestingModal;
